import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { useRetryPaymentTransfer } from '@/hooks/usePaymentRetryAdmin';
import { usePaymentApprovals } from '@/hooks/usePaymentApprovals';
import { AlertTriangle, XCircle, RefreshCw, DollarSign, FileText, ClipboardCheck, Clock, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';

interface FailedPayoutItem {
  id: string;
  job_id: string;
  status: 'failed' | 'failed_permanently';
  last_error: string | null;
  retry_count: number;
  max_retries: number;
  next_retry_at: string | null;
  last_attempt_at: string | null;
  created_at: string;
  updated_at: string;
  jobs?: {
    id: string;
    public_id: string;
    title: string;
    status: string;
    payment_status: string;
  };
  payment_transactions?: {
    id: string;
    vendor_payout_cents: number;
    total_amount_cents: number;
    status: string;
  };
}

export function FailedPayoutsSection() {
  const queryClient = useQueryClient();
  const { mutate: retryTransfer, isPending: isRetrying } = useRetryPaymentTransfer();
  const { manualCompletePayoutMutation } = usePaymentApprovals();
  
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [showManualCompleteDialog, setShowManualCompleteDialog] = useState(false);
  const [manualCompleteItem, setManualCompleteItem] = useState<FailedPayoutItem | null>(null);
  const [manualReason, setManualReason] = useState('');

  const { data: failedPayouts, isLoading } = useQuery({
    queryKey: ['payment-approvals', 'failed-payouts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payment_transfer_queue')
        .select(`
          *,
          jobs!inner (
            id,
            public_id,
            title,
            status,
            payment_status
          ),
          payment_transactions!inner (
            id,
            vendor_payout_cents,
            total_amount_cents,
            status
          )
        `)
        .eq('requires_approval', true)
        .in('status', ['failed', 'failed_permanently'])
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return data as FailedPayoutItem[];
    },
  });

  const handleRetry = (item: FailedPayoutItem) => {
    setRetryingId(item.id);
    retryTransfer({ queueId: item.id }, {
      onSettled: () => {
        setRetryingId(null);
        // Also invalidate the failed-payouts query
        queryClient.invalidateQueries({ queryKey: ['payment-approvals', 'failed-payouts'] });
        queryClient.invalidateQueries({ queryKey: ['payment-approvals', 'pending_approval'] });
      }
    });
  };

  const handleManualComplete = (item: FailedPayoutItem) => {
    setManualCompleteItem(item);
    setManualReason('');
    setShowManualCompleteDialog(true);
  };

  const confirmManualComplete = () => {
    if (!manualCompleteItem || !manualReason.trim()) return;
    
    manualCompletePayoutMutation.mutate({
      queueId: manualCompleteItem.id,
      notes: '',
      reason: manualReason,
    }, {
      onSuccess: () => {
        setShowManualCompleteDialog(false);
        setManualCompleteItem(null);
        setManualReason('');
        // Invalidate queries to refresh the lists
        queryClient.invalidateQueries({ queryKey: ['payment-approvals', 'failed-payouts'] });
        queryClient.invalidateQueries({ queryKey: ['payment-approvals', 'pending_approval'] });
      }
    });
  };

  if (isLoading) {
    return null; // Don't show loading state to avoid UI flash
  }

  if (!failedPayouts || failedPayouts.length === 0) {
    return null; // Don't show empty section
  }

  const formatAmount = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  return (
    <div className="space-y-4">
      {/* Alert banner */}
      <Alert variant="destructive" className="border-destructive/50 bg-destructive/10">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription className="flex items-center justify-between">
          <span>
            <strong>{failedPayouts.length} failed payout{failedPayouts.length > 1 ? 's' : ''}</strong> need attention. 
            These payouts failed during approval and require manual intervention.
          </span>
        </AlertDescription>
      </Alert>

      {/* Failed payout cards */}
      {failedPayouts.map((payout) => {
        const job = payout.jobs;
        const transaction = payout.payment_transactions;
        const isPermanentlyFailed = payout.status === 'failed_permanently';
        const isThisRetrying = retryingId === payout.id && isRetrying;

        return (
          <Card key={payout.id} className="border-l-4 border-l-destructive">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Failed Payout - {job?.public_id}
                  </CardTitle>
                  <CardDescription>{job?.title}</CardDescription>
                </div>
                <Badge 
                  variant={isPermanentlyFailed ? 'destructive' : 'outline'}
                  className={isPermanentlyFailed ? '' : 'border-orange-500 text-orange-600 bg-orange-50 dark:bg-orange-950/20'}
                >
                  {isPermanentlyFailed ? (
                    <>
                      <XCircle className="h-3 w-3 mr-1" />
                      Failed Permanently
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Failed - Retry Available
                    </>
                  )}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Amount display */}
              {transaction && (
                <div className="bg-muted/50 p-3 rounded-lg">
                  <p className="text-sm text-muted-foreground">Vendor Payout Amount</p>
                  <p className="text-2xl font-bold">{formatAmount(transaction.vendor_payout_cents)}</p>
                </div>
              )}

              {/* Error message */}
              {payout.last_error && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <div className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-destructive">Error Details</p>
                      <p className="text-sm text-muted-foreground mt-1">{payout.last_error}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Retry info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Retry Attempts</p>
                  <p className="font-medium">{payout.retry_count} / {payout.max_retries}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Last Attempt</p>
                  <p className="font-medium">
                    {payout.last_attempt_at 
                      ? format(new Date(payout.last_attempt_at), 'PPp')
                      : 'Never'}
                  </p>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex flex-wrap gap-2 pt-2 border-t">
                <Button
                  variant="default"
                  onClick={() => handleRetry(payout)}
                  disabled={isThisRetrying}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${isThisRetrying ? 'animate-spin' : ''}`} />
                  {isThisRetrying ? 'Retrying...' : 'Retry Payout'}
                </Button>
                <Button
                  variant="outline"
                  className="border-amber-500 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/20"
                  onClick={() => handleManualComplete(payout)}
                  disabled={manualCompletePayoutMutation.isPending}
                >
                  <ClipboardCheck className="h-4 w-4 mr-2" />
                  Mark as Manually Completed
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.open(`/jobs/${job?.id}`, '_blank')}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  View Job
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Manual Complete Dialog */}
      <Dialog open={showManualCompleteDialog} onOpenChange={setShowManualCompleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Payout as Manually Completed</DialogTitle>
            <DialogDescription>
              This will mark the payout as completed without processing through Stripe. 
              The vendor will NOT receive funds through the platform. Use this only when 
              the payment was processed manually (e.g., bank wire transfer).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="failed-manual-reason">Completion Reason (Required)</Label>
              <Textarea
                id="failed-manual-reason"
                placeholder="e.g., Processed via bank wire transfer on 01/15/2026, confirmation #ABC123"
                value={manualReason}
                onChange={(e) => setManualReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualCompleteDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={confirmManualComplete}
              disabled={!manualReason.trim() || manualCompletePayoutMutation.isPending}
            >
              {manualCompletePayoutMutation.isPending ? 'Processing...' : 'Confirm Manual Completion'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
